/**
 * REST API client for CareArena frontend.
 *
 * Replaces the former Supabase JS client with direct REST calls to the
 * FastAPI backend.  All public function names are preserved so existing
 * callers require no changes.
 */

import axios from 'axios'
import { clearToken, getToken, setToken } from '../utils/tokenStore'

// In dev, requests go to /api/... and Vite proxies them to the backend.
// In production, set VITE_API_BASE_URL to the backend origin (e.g. https://api.example.com).
const BASE_URL = import.meta.env.VITE_API_BASE_URL || ''
const API_KEY = import.meta.env.VITE_API_KEY || ''

export const isApiConfigured = true

/** Alias so callers that import isSupabaseConfigured still work. */
export const isSupabaseConfigured = isApiConfigured

// ---------------------------------------------------------------------------
// Axios instance
// ---------------------------------------------------------------------------

// The "/api" fallback lives on the instance, not on a helper. Pages import this
// instance directly and call bare paths (`api.post('/patients')`), so a helper
// that only the wrappers below used left those calls pointing at the frontend's
// own origin: GETs silently returned index.html and writes returned 405.
const api = axios.create({ baseURL: BASE_URL || '/api' })

/**
 * Path passthrough. The /api prefix is now part of `baseURL`, so this is the
 * identity function — kept so existing call sites keep reading naturally.
 */
const p = (path) => path

/** Attach API key and optional JWT Bearer token to every request. */
api.interceptors.request.use((config) => {
  if (API_KEY) {
    config.headers['X-API-Key'] = API_KEY
  }
  const token = getToken()
  if (token) {
    config.headers['Authorization'] = `Bearer ${token}`
  }
  return config
})

// ---------------------------------------------------------------------------
// Auth helpers
// ---------------------------------------------------------------------------

export async function login(email, password, { remember = true } = {}) {
  const { data } = await api.post(p('/auth/login'), { email, password })
  if (data.access_token) {
    setToken(data.access_token, { remember })
  }
  return data
}

export async function register(email, password, fullName = '', { remember = true } = {}) {
  const { data } = await api.post(p('/auth/register'), { email, password, full_name: fullName })
  if (data.access_token) {
    setToken(data.access_token, { remember })
  }
  return data
}

export function logout() {
  clearToken()
}

export async function getMe() {
  const { data } = await api.get(p('/auth/me'))
  return data
}

/**
 * Update the signed-in user's own profile. Returns the updated user.
 *
 * PUT rather than PATCH: CORS_ALLOW_METHODS is deployment configuration and its
 * default did not include PATCH, so the preflight for this route returned 400
 * and the save failed with an opaque "Network Error". Every save sends the
 * whole editable set anyway. The server accepts both verbs.
 */
export async function updateProfile(fields) {
  const { data } = await api.put(p('/auth/me'), fields)
  return data
}

/** Upload a profile picture. Returns the updated user, including avatar_url. */
export async function uploadAvatar(file) {
  const form = new FormData()
  form.append('file', file)
  // No explicit Content-Type: the browser has to set the multipart boundary,
  // and naming the type by hand omits it and the upload fails to parse.
  const { data } = await api.post(p('/auth/me/avatar'), form)
  return data
}

/**
 * Absolute URL for a stored avatar.
 *
 * avatar_url is stored root-relative ("/static/avatars/…") so the value stays
 * correct if the backend moves. In dev the app is served from Vite on a
 * different port than the API, so it needs the API origin prefixed; in
 * production BASE_URL is empty and the path is already right.
 */
export function avatarSrc(url) {
  if (!url) return null
  if (/^(https?:|data:|blob:)/.test(url)) return url
  return `${BASE_URL}${url}`
}

// ---------------------------------------------------------------------------
// Patients
// ---------------------------------------------------------------------------

/**
 * Fetch patients from the backend.
 * @param {Object} filters - Optional filters (search, limit, user_id)
 */
export async function getPatients(filters = {}) {
  try {
    const params = {}
    if (filters.search) params.search = filters.search
    if (filters.limit) params.limit = filters.limit
    if (filters.user_id) params.user_id = filters.user_id
    const { data } = await api.get(p('/patients'), { params })
    return data.patients ?? data ?? []
  } catch (error) {
    console.error('Error fetching patients:', error)
    throw error
  }
}

// ---------------------------------------------------------------------------
// Campaigns
// ---------------------------------------------------------------------------

/**
 * Fetch campaigns from the backend.
 * @param {Object} filters - Optional filters (status, type, user_id)
 */
export async function getCampaigns(filters = {}) {
  try {
    const params = {}
    if (filters.status) params.status = filters.status
    if (filters.type) params.type = filters.type
    if (filters.user_id) params.user_id = filters.user_id
    const { data } = await api.get(p('/campaigns'), { params })
    return data.campaigns ?? data ?? []
  } catch (error) {
    console.error('Error fetching campaigns:', error)
    throw error
  }
}

/**
 * Create a new campaign.
 * @param {Object} campaignData
 */
export async function createCampaign(campaignData) {
  try {
    const { data } = await api.post(p('/campaigns'), campaignData)
    return data.campaign ?? data
  } catch (error) {
    console.error('Error creating campaign:', error)
    throw error
  }
}

/**
 * Update a campaign.
 * @param {string} campaignId
 * @param {Object} updates
 */
export async function updateCampaign(campaignId, updates) {
  try {
    const { data } = await api.put(p(`/campaigns/${campaignId}`), updates)
    return data.campaign ?? data
  } catch (error) {
    console.error('Error updating campaign:', error)
    throw error
  }
}

// ---------------------------------------------------------------------------
// Analytics
// ---------------------------------------------------------------------------

/**
 * Get the global summary (patients, campaigns, interactions).
 */
export async function getGlobalSummary() {
  try {
    const { data } = await api.get(p('/analytics/global-summary'))
    return data
  } catch (error) {
    console.error('Error fetching global summary:', error)
    return null
  }
}

/**
 * Get weekly engagement data (last 4 weeks).
 */
export async function getWeeklyEngagement() {
  try {
    const { data } = await api.get(p('/analytics/weekly-engagement'))
    return data?.data ?? data ?? []
  } catch (error) {
    console.error('Error fetching weekly engagement:', error)
    return []
  }
}

/**
 * Get campaign statistics.
 * @param {string|null} campaignId
 */
export async function getCampaignStats(campaignId = null) {
  try {
    const params = campaignId ? { campaign_id: campaignId } : {}
    const { data } = await api.get(p('/analytics/campaign-stats'), { params })
    return data?.data ?? data ?? []
  } catch (error) {
    console.error('Error fetching campaign stats:', error)
    return []
  }
}

/**
 * Get feedback statistics from campaign interactions.
 * @param {string|null} campaignId
 */
export async function getFeedbackStats(campaignId = null) {
  try {
    const params = campaignId ? { campaign_id: campaignId } : {}
    const { data } = await api.get(p('/analytics/feedback-stats'), { params })
    return data ?? { total: 0, avg: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, recent: [] }
  } catch (error) {
    console.error('Error fetching feedback stats:', error)
    return { total: 0, avg: 0, distribution: { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }, recent: [] }
  }
}

/**
 * Get ElevenLabs conversations.
 * @param {Object} filters - Optional filters (campaign_id, limit)
 */
export async function getElevenLabsConversations(filters = {}) {
  try {
    const params = {}
    if (filters.campaign_id) params.campaign_id = filters.campaign_id
    if (filters.limit) params.limit = filters.limit
    const { data } = await api.get(p('/analytics/elevenlabs/conversations'), { params })
    return data?.conversations ?? data ?? []
  } catch (error) {
    console.error('Error fetching ElevenLabs conversations:', error)
    return []
  }
}

// ---------------------------------------------------------------------------
// Real-time stub  (Supabase real-time removed — use polling instead)
// ---------------------------------------------------------------------------

/**
 * Subscribe to table changes.
 * Returns a stub subscription with a no-op unsubscribe, since we no longer
 * use Supabase real-time.  Callers should poll via setInterval instead.
 */
export function subscribeToTable(_table, _callback) {
  return { unsubscribe: () => {} }
}

/**
 * Subscribe to analytics updates (stub).
 */
export function subscribeToAnalyticsUpdates(_callback) {
  return { unsubscribe: () => {} }
}

export default api
